import React from 'react';
import { motion } from 'framer-motion';
import { Store } from 'lucide-react';
import { SectionHeader } from './shared/SectionHeader';
import { PlatformLogo } from './shared/PlatformLogo';
import { SkeletonLoader } from './shared/SkeletonLoader';
import { formatCurrencyCompact, formatCurrencyUnit, formatStat, formatLocalDate } from '../utils/formatters';

function EcommerceCard({ platform, name, color, summary, dailyRows, isLoading }) {
  if (isLoading) return <SkeletonLoader variant="card" />;
  if (!summary) return null;

  const rows = (dailyRows || []).slice(0, 10);

  return (
    <div className="glass-card rounded-2xl overflow-hidden" style={{ borderTop: `3px solid ${color}` }}>
      <div className="p-5">
        <div className="flex items-center gap-2 mb-4">
          <PlatformLogo platform={platform} size="md" />
          <h3 className="text-sm font-semibold text-gray-800">{name}</h3>
        </div>

        {/* Summary metrics */}
        <div className="grid grid-cols-3 gap-3 mb-4">
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Revenue</p>
            <p className="text-lg font-bold text-gray-900">{formatCurrencyCompact(summary.revenue || 0)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">Orders</p>
            <p className="text-lg font-bold text-gray-900">{formatStat(summary.orders || summary.purchases || 0)}</p>
          </div>
          <div>
            <p className="text-[10px] uppercase tracking-wider text-gray-400 font-medium">{summary.items != null ? 'Items' : summary.installs != null ? 'Installs' : 'Sessions'}</p>
            <p className="text-lg font-bold text-gray-900">{formatStat(summary.items || summary.installs || summary.sessions || 0)}</p>
          </div>
        </div>

        {/* Mini daily table */}
        {rows.length > 0 && (
          <div className="border-t border-gray-100 pt-3">
            <div className="max-h-[200px] overflow-y-auto">
              <table className="w-full">
                <thead>
                  <tr>
                    <th className="text-left text-[10px] font-semibold text-gray-400 uppercase tracking-wider pb-2">Date</th>
                    <th className="text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider pb-2">Revenue</th>
                    <th className="text-right text-[10px] font-semibold text-gray-400 uppercase tracking-wider pb-2">Orders</th>
                  </tr>
                </thead>
                <tbody>
                  {rows.map((row, i) => {
                    const dateStr = typeof row.date === 'string' ? row.date.split('T')[0] : '';
                    return (
                      <tr key={i} className="border-t border-gray-50">
                        <td className="py-1.5 text-xs text-gray-600">{dateStr ? formatLocalDate(dateStr) : ''}</td>
                        <td className="py-1.5 text-right text-xs font-medium">{formatCurrencyUnit(row.revenue || row.totalRevenue || 0)}</td>
                        <td className="py-1.5 text-right text-xs">{formatStat(row.orders || row.orderCount || 0)}</td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function EcommercePlatforms({
  ikasSummary, ikasDailyRows,
  shopifySummary, shopifyDailyRows,
  wooSummary, wooDailyRows,
  adjustSummary, adjustDailyRows,
  hasIkasConnected, hasShopifyConnected, hasWooConnected, hasAdjustConnected,
  isLoading,
}) {
  const platforms = [
    hasIkasConnected && { platform: 'ikas', name: 'IKAS', color: '#6366F1', summary: ikasSummary, dailyRows: ikasDailyRows },
    hasShopifyConnected && { platform: 'shopify', name: 'Shopify', color: '#96BF48', summary: shopifySummary, dailyRows: shopifyDailyRows },
    hasWooConnected && { platform: 'woo', name: 'WooCommerce', color: '#96588A', summary: wooSummary, dailyRows: wooDailyRows },
    hasAdjustConnected && { platform: 'adjust', name: 'Adjust', color: '#FF4C3B', summary: adjustSummary, dailyRows: adjustDailyRows },
  ].filter(Boolean);

  if (platforms.length === 0) return null;

  return (
    <motion.div
      initial={{ opacity: 0, y: 16 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, margin: '-50px' }}
      transition={{ duration: 0.5 }}
      className="mb-6"
    >
      <SectionHeader title="E-commerce Platforms" icon={Store} />
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        {platforms.map((p) => (
          <EcommerceCard key={p.platform} {...p} isLoading={isLoading?.[p.platform.replace('-', '')]} />
        ))}
      </div>
    </motion.div>
  );
}
